import { Controller } from "@hotwired/stimulus";

// Dropdown de villes au fil de la saisie. Le champ reste un texte libre (city_text) ;
// la liste aide à choisir une ville géolocalisable.
export default class extends Controller {
  static targets = ["list"];
  static values = { url: String };

  connect() {
    this.input = this.element.querySelector("input[name$='[city_text]']");
    if (!this.input) return;
    this.timer = null;
    this.controller = null;
    this.active = -1;
    this.input.setAttribute("role", "combobox");
    this.input.setAttribute("aria-autocomplete", "list");
    this.input.setAttribute("aria-expanded", "false");
    if (!this.listTarget.id) this.listTarget.id = `city-suggest-${Math.random().toString(36).slice(2)}`;
    this.input.setAttribute("aria-controls", this.listTarget.id);

    this.onInput = () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.fetch(), 300);
    };
    this.onBlur = () => setTimeout(() => this.close(), 150);
    this.onKeydown = (e) => this.keydown(e);
    this.input.addEventListener("input", this.onInput);
    this.input.addEventListener("blur", this.onBlur);
    this.input.addEventListener("keydown", this.onKeydown);
  }

  disconnect() {
    if (!this.input) return;
    clearTimeout(this.timer);
    if (this.controller) this.controller.abort();
    this.input.removeEventListener("input", this.onInput);
    this.input.removeEventListener("blur", this.onBlur);
    this.input.removeEventListener("keydown", this.onKeydown);
  }

  async fetch() {
    const q = this.input.value.trim();
    if (this.controller) this.controller.abort();
    if (q.length < 2) return this.close();
    this.controller = new AbortController();
    try {
      const response = await window.fetch(`${this.urlValue}?q=${encodeURIComponent(q)}`, {
        headers: { Accept: "application/json" },
        signal: this.controller.signal,
      });
      if (!response.ok) return this.close();
      const items = await response.json();
      // Réponse périmée : la saisie a changé entre-temps.
      if (this.input.value.trim() !== q) return;
      this.render(items);
    } catch (e) {
      if (e.name !== "AbortError") this.close();
    }
  }

  keydown(e) {
    if (this.listTarget.hidden) return;
    const options = this.options();
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (options.length === 0) return;
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      this.setActive((this.active + step + options.length) % options.length);
    } else if (e.key === "Enter") {
      if (this.active >= 0 && options[this.active]) {
        e.preventDefault();
        this.choose(options[this.active].dataset.value);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      this.close();
    }
  }

  options() {
    return Array.from(this.listTarget.querySelectorAll("[role='option']"));
  }

  setActive(index) {
    const options = this.options();
    options.forEach((o, i) => {
      const on = i === index;
      o.setAttribute("aria-selected", on ? "true" : "false");
      o.classList.toggle("bg-gray-100", on);
    });
    this.active = index;
    if (options[index]) {
      this.input.setAttribute("aria-activedescendant", options[index].id);
      options[index].scrollIntoView({ block: "nearest" });
    } else {
      this.input.removeAttribute("aria-activedescendant");
    }
  }

  choose(value) {
    this.input.value = value;
    this.close();
  }

  render(items) {
    this.listTarget.replaceChildren();
    this.active = -1;
    this.input.removeAttribute("aria-activedescendant");
    if (items.length === 0) {
      const li = document.createElement("li");
      li.className = "px-3 py-2 text-gray-500";
      li.textContent = "Aucune ville trouvée — vérifiez l'orthographe";
      this.listTarget.appendChild(li);
    }
    items.forEach((item, i) => {
      const li = document.createElement("li");
      li.id = `${this.listTarget.id}-opt-${i}`;
      li.setAttribute("role", "option");
      li.setAttribute("aria-selected", "false");
      li.dataset.value = item.value;
      li.className = "cursor-pointer px-3 py-2 hover:bg-gray-100";
      const name = document.createElement("span");
      name.className = "font-medium text-slate-900";
      name.textContent = item.label;
      const meta = document.createElement("span");
      meta.className = "ml-2 text-gray-500";
      meta.textContent = [item.zip_code, item.department].filter(Boolean).join(" · ");
      li.append(name, meta);
      li.addEventListener("mousedown", (e) => {
        e.preventDefault();
        this.choose(item.value);
      });
      this.listTarget.appendChild(li);
    });
    this.listTarget.hidden = false;
    this.input.setAttribute("aria-expanded", "true");
  }

  close() {
    this.listTarget.hidden = true;
    this.active = -1;
    this.input.setAttribute("aria-expanded", "false");
    this.input.removeAttribute("aria-activedescendant");
  }
}
